"use client";

import React, { useMemo } from "react";
import type { LucideIcon } from "lucide-react";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@west-tennessee-consulting/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  INTENT_BUTTON_CLASSES,
  INTENT_BUTTON_VARIANT,
  type ActionIntent,
} from "./action-intent";

/** How many actions stay as buttons before the rest collapse into "More". */
const DEFAULT_MAX_VISIBLE_ACTIONS = 5;

export interface EntityAction {
  /** Stable key; also used as the test id suffix. */
  id: string;
  label: string;
  onSelect: () => void;
  /** Sentiment — drives variant + colour. Defaults to `neutral`. */
  intent?: ActionIntent;
  icon?: LucideIcon;
  disabled?: boolean;
  /** Explains a disabled action, or adds context to an enabled one. */
  title?: string;
  /**
   * Keep this action out of the overflow menu — it always renders as a button.
   * The primary action is pinned automatically.
   */
  pinned?: boolean;
}

interface EntityActionBarProps {
  actions: EntityAction[];
  /** Buttons shown before overflow. Defaults to {@link DEFAULT_MAX_VISIBLE_ACTIONS}. */
  maxVisible?: number;
  className?: string;
  /** Names the group for assistive tech, e.g. "Deal actions". */
  ariaLabel?: string;
}

/**
 * The shared detail-page action bar.
 *
 * Two problems it fixes, everywhere it is used:
 *
 *  1. **No hierarchy.** Every action used to be an identical outline button, so
 *     a rep scanned eight equal-weight controls to find the one they wanted.
 *     Actions now declare an intent (see `action-intent.ts`) and the primary one
 *     is visually distinct.
 *  2. **Inconsistent sentiment.** Green/red were hand-typed per screen and
 *     frequently missed dark mode. The mapping lives in one place now.
 *
 * Actions beyond `maxVisible` collapse into an overflow menu rather than
 * wrapping into a wall — but the primary action and anything `pinned` always
 * stay visible.
 */
export function EntityActionBar({
  actions,
  maxVisible = DEFAULT_MAX_VISIBLE_ACTIONS,
  className,
  ariaLabel = "Actions",
}: EntityActionBarProps) {
  const { visibleActions, overflowActions } = useMemo(() => {
    const enabled = actions.filter(Boolean);
    if (enabled.length <= maxVisible) {
      return { visibleActions: enabled, overflowActions: [] as EntityAction[] };
    }

    // Pinned + primary keep their slots; the rest fill the remaining space in
    // declaration order, so an overflowing bar never hides the main action.
    const mustShow = enabled.filter((action) => action.pinned || action.intent === "primary");
    const rest = enabled.filter((action) => !mustShow.includes(action));
    const remainingSlots = Math.max(0, maxVisible - mustShow.length);

    const promoted = rest.slice(0, remainingSlots);
    const visible = enabled.filter(
      (action) => mustShow.includes(action) || promoted.includes(action)
    );

    return {
      visibleActions: visible,
      overflowActions: rest.slice(remainingSlots),
    };
  }, [actions, maxVisible]);

  if (visibleActions.length === 0 && overflowActions.length === 0) return null;

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2 md:gap-3", className)}
      role="group"
      aria-label={ariaLabel}
    >
      {visibleActions.map((action) => {
        const intent = action.intent ?? "neutral";
        const Icon = action.icon;
        return (
          <Button
            key={action.id}
            type="button"
            size="sm"
            variant={INTENT_BUTTON_VARIANT[intent]}
            onClick={action.onSelect}
            disabled={action.disabled}
            title={action.title}
            data-testid={`entity-action-${action.id}`}
            data-intent={intent}
            className={cn(
              "text-xs flex-1 sm:flex-none",
              INTENT_BUTTON_CLASSES[intent],
              // The primary action gets the only filled button in the bar, so
              // it reads at a glance.
              intent === "primary" && "font-semibold"
            )}
          >
            {Icon && <Icon className="mr-1.5 h-3.5 w-3.5" aria-hidden />}
            {action.label}
          </Button>
        );
      })}

      {overflowActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-xs"
              aria-label="More actions"
              title="More actions"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {overflowActions.map((action) => {
              const Icon = action.icon;
              return (
                <DropdownMenuItem
                  key={action.id}
                  onClick={action.onSelect}
                  disabled={action.disabled}
                  data-testid={`entity-action-${action.id}`}
                >
                  {Icon && <Icon className="mr-2 h-4 w-4" aria-hidden />}
                  {action.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
